/**
 * The apothecary's small models and surface drawings (lifted from
 * lantern-water's `apothecary.js`): four lathe-turned vessel profiles with
 * labels that follow their contour, lids, a glazed ceramic jar with banded
 * rings, the brass counter carving, and the painted cabinet finish. The
 * prototype's module-level textures, materials and lathe bodies are kit-shared
 * here (`kit.shared('apothecary:<name>', ...)`), made once per view. The
 * labels keep their frames and plant drawings; the herb names they carried are
 * ruled ink lines (no text in textures, ADR 0005).
 */
import * as THREE from 'three';
import type { Ctx, Kit, SceneMaterials } from './kit.ts';

/** A candle in its brass dish, its flame and halo (`world.js` `candle`), standing at (x, y, z). */
export const candle = (ctx: Ctx, p: THREE.Object3D, x: number, y: number, z: number): void => {
  ctx.cylinder(p, x, y + 0.035, z, 0.16, 0.06, ctx.m.brass);
  ctx.cylinder(p, x, y + 0.19, z, 0.065, 0.32, ctx.m.canvas);
  ctx.mesh(p, ctx.sphere, ctx.m.glass, x, y + 0.41, z, 0.055, 0.13, 0.045);
  ctx.glow(p, x, y + 0.42, z, 0.95);
};

const noise = (x: number, y: number): number => {
  const v = Math.sin(x * 17.13 + y * 73.51) * 43758.5453;
  return v - Math.floor(v);
};

const canvasMap = (width: number, height: number, paint: (g: CanvasRenderingContext2D) => void): THREE.CanvasTexture => {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const g = c.getContext('2d');
  if (g === null) throw new Error('no 2D canvas context for the apothecary textures');
  paint(g);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestMipmapLinearFilter;
  return t;
};

const glaze = (kit: Kit): THREE.Texture =>
  kit.shared('apothecary:glaze', () =>
    canvasMap(128, 128, (g) => {
      g.fillStyle = '#d7d8cb';
      g.fillRect(0, 0, 128, 128);
      for (let y = 0; y < 128; y += 1) {
        for (let x = 0; x < 128; x += 1) {
          g.fillStyle = noise(x, y) > 0.6 ? '#fff6df0a' : '#263c380b';
          g.fillRect(x, y, 1, 1);
        }
      }
      g.fillStyle = '#f5f5dd62';
      g.fillRect(4, 19, 3, 75);
      g.fillStyle = '#ffffee3b';
      g.fillRect(7, 24, 2, 66);
    }),
  );

const labels = (kit: Kit): THREE.Texture =>
  kit.shared('apothecary:labels', () =>
    canvasMap(256, 128, (g) => {
      const inks = ['#694e37', '#58664b', '#365b6b', '#425b4a'];
      const papers = ['#e2cfa4', '#e4d6b1', '#e6ddbe', '#c4d5c6'];
      // The herb names' widths, drawn as a ruled ink line where the word was.
      const nameWidths = [30, 34, 36, 32];
      for (let i = 0; i < 4; i += 1) {
        const x = i * 64;
        const ink = inks[i] ?? '#694e37';
        g.fillStyle = papers[i] ?? '#e2cfa4';
        g.fillRect(x, 0, 64, 128);
        for (let j = 0; j < 600; j += 1) {
          g.fillStyle = j % 2 ? '#fff2c525' : '#57452b17';
          g.fillRect(x + noise(j, i) * 64, noise(i, j) * 128, 1, 2);
        }
        g.strokeStyle = ink;
        g.lineWidth = 2;
        g.strokeRect(x + 4, 7, 56, 114);
        g.lineWidth = 1;
        g.strokeRect(x + 7, 11, 50, 106);
        g.beginPath();
        g.moveTo(x + 32, 87);
        g.bezierCurveTo(x + 23, 65, x + 37, 51, x + 31, 31);
        g.stroke();
        g.fillStyle = ink;
        for (let leaf = 0; leaf < 6; leaf += 1) {
          const yy = 38 + leaf * 7;
          const side = leaf % 2 ? -1 : 1;
          g.beginPath();
          g.ellipse(x + 31 + side * 7, yy, 8, 3, side * -0.55, 0, Math.PI * 2);
          g.fill();
        }
        const w = nameWidths[i] ?? 32;
        g.fillRect(x + 32 - w / 2, 98, w, 2);
        g.fillRect(x + 19, 108, 26, 1);
        g.fillRect(x + 26, 23, 12, 2);
      }
    }),
  );

/** The painted cabinet finish of the counter and the joinery (`apothecary.js` `cabinetPaint`). */
export const cabinetPaint = (kit: Kit): THREE.MeshStandardMaterial =>
  kit.shared('apothecary:cabinet-paint', () => {
    const paintMap = kit.shared('apothecary:paint-map', () =>
      canvasMap(128, 128, (g) => {
        g.fillStyle = '#d3d6ca';
        g.fillRect(0, 0, 128, 128);
        for (let x = 0; x < 128; x += 1) {
          g.fillStyle = x % 3 ? '#f7ebcc16' : '#283d3e23';
          g.fillRect(x, noise(x, 1) * 30, 1, 40 + noise(x, 2) * 60);
        }
        for (let i = 0; i < 75; i += 1) {
          g.fillStyle = '#79807150';
          g.fillRect(noise(i, 5) * 128, noise(i, 6) * 128, 1, 2 + noise(i, 7) * 4);
        }
      }),
    );
    return new THREE.MeshStandardMaterial({ color: '#758b78', map: paintMap, roughness: 0.78 });
  });

const profiles: readonly (readonly (readonly [number, number])[])[] = [
  [
    [0, 0],
    [0.29, 0],
    [0.36, 0.07],
    [0.37, 0.92],
    [0.34, 1.08],
    [0.17, 1.27],
    [0.14, 1.55],
    [0.18, 1.56],
    [0.18, 1.66],
    [0, 1.66],
  ],
  [
    [0, 0],
    [0.28, 0],
    [0.41, 0.12],
    [0.48, 0.52],
    [0.44, 0.94],
    [0.29, 1.2],
    [0.17, 1.32],
    [0.17, 1.65],
    [0.22, 1.66],
    [0.22, 1.78],
    [0, 1.78],
  ],
  [
    [0, 0],
    [0.41, 0],
    [0.47, 0.06],
    [0.47, 0.17],
    [0.43, 0.23],
    [0.38, 0.56],
    [0.4, 1.05],
    [0.45, 1.22],
    [0.45, 1.34],
    [0.49, 1.37],
    [0.49, 1.44],
    [0, 1.44],
  ],
  [
    [0, 0],
    [0.31, 0],
    [0.38, 0.07],
    [0.39, 1.18],
    [0.3, 1.3],
    [0.15, 1.39],
    [0.15, 1.81],
    [0.19, 1.82],
    [0.19, 1.9],
    [0, 1.9],
  ],
];
const labelShapes: readonly (readonly [number, number])[] = [
  [0.63, 0.56],
  [0.68, 0.53],
  [0.75, 0.66],
  [0.71, 0.7],
];
const palette = ['#62998e', '#b68a46', '#e0d3ab', '#547889'];

const add = (p: THREE.Object3D, geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number, scale = 1): THREE.Mesh => {
  const o = new THREE.Mesh(geometry, material);
  o.position.set(x, y, z);
  o.scale.setScalar(scale);
  o.castShadow = true;
  o.receiveShadow = true;
  p.add(o);
  return o;
};

/** A vessel of `variant % 4` (bottle, flask, ceramic jar, tall bottle) at (x, y, z), `scale` metres per profile unit. */
export const apothecaryVessel = (kit: Kit, p: THREE.Object3D, x: number, y: number, z: number, scale: number, variant: number): void => {
  const m: SceneMaterials = kit.m;
  const type = ((variant % 4) + 4) % 4;
  const profile = profiles[type] ?? profiles[0] ?? [];
  const vessel = kit.shared('apothecary:vessel', () => new THREE.MeshStandardMaterial({ map: glaze(kit), color: '#ffffff', vertexColors: true, roughness: 0.26, metalness: 0.06 }));
  const ceramic = kit.shared('apothecary:ceramic', () => new THREE.MeshStandardMaterial({ map: glaze(kit), color: '#ded7b5', roughness: 0.36 }));
  const label = kit.shared('apothecary:label', () => new THREE.MeshStandardMaterial({ map: labels(kit), roughness: 0.87 }));
  const base = kit.shared(`apothecary:body:${type}`, () => new THREE.LatheGeometry(profile.map(([px, py]) => new THREE.Vector2(px, py)), 16));
  const body = base.clone();
  if (type !== 2) {
    const color = new THREE.Color(palette[type] ?? '#62998e');
    const colors: number[] = [];
    for (let i = 0; i < body.getAttribute('position').count; i += 1) colors.push(color.r, color.g, color.b);
    body.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  }
  add(p, body, type === 2 ? ceramic : vessel, x, y, z, scale);
  const [cy, h] = labelShapes[type] ?? [0.63, 0.56];
  const lo = cy - h / 2;
  const hi = cy + h / 2;
  const radiusAt = (height: number): number => {
    for (let i = 1; i < profile.length; i += 1) {
      const [ra, ya] = profile[i - 1] ?? [0, 0];
      const [rb, yb] = profile[i] ?? [0, 0];
      if (yb > ya && height >= ya && height <= yb) return ra + (rb - ra) * ((height - ya) / (yb - ya)) + 0.006;
    }
    throw new Error('Label outside vessel contour');
  };
  const heights = [lo, ...profile.map((point) => point[1]).filter((height) => height > lo && height < hi), hi];
  const labelGeo = new THREE.LatheGeometry(
    heights.map((height) => new THREE.Vector2(radiusAt(height), height)),
    12,
    -0.94,
    1.88,
  );
  const uv = labelGeo.getAttribute('uv');
  const pos = labelGeo.getAttribute('position');
  for (let i = 0; i < uv.count; i += 1) {
    uv.setX(i, (type + uv.getX(i) * 0.96 + 0.02) / 4);
    uv.setY(i, (pos.getY(i) - lo) / h);
  }
  add(p, labelGeo, label, x, y, z, scale);
  const top = profile.at(-1)?.[1] ?? 0;
  const lid =
    type === 2
      ? kit.shared(
          'apothecary:jar-lid',
          () =>
            new THREE.LatheGeometry(
              (
                [
                  [0, 0],
                  [0.49, 0],
                  [0.49, 0.07],
                  [0.38, 0.13],
                  [0.13, 0.18],
                  [0.1, 0.26],
                  [0, 0.27],
                ] as const
              ).map(([a, b]) => new THREE.Vector2(a, b)),
              16,
            ),
        )
      : kit.shared(`apothecary:stopper:${type === 1 ? 'wide' : 'narrow'}`, () => new THREE.CylinderGeometry(type === 1 ? 0.18 : 0.135, type === 1 ? 0.19 : 0.15, 0.15, 12));
  add(p, lid, type === 2 ? ceramic : m.wood, x, y + (top + (type === 2 ? 0 : 0.055)) * scale, z, scale);
  if (type === 2) {
    for (const yy of [0.18, 1.25]) {
      const ring = kit.shared(`apothecary:jar-ring:${yy}`, () => new THREE.TorusGeometry(yy < 1 ? 0.459 : 0.453, 0.026, 4, 16));
      const o = add(p, ring, m.book1, x, y + yy * scale, z, scale);
      o.rotation.x = Math.PI / 2;
    }
  }
};

/** The brass sprig carved on a counter panel; even variants carry an oval ring. */
export const counterCarving = (kit: Kit, p: THREE.Object3D, x: number, y: number, z: number, variant: number): void => {
  const m = kit.m;
  const center = new THREE.Group();
  center.position.set(x, y, z);
  p.add(center);
  const stem = kit.shared('apothecary:carving-stem', () => {
    const curve = new THREE.CatmullRomCurve3([new THREE.Vector3(0, -0.19, 0), new THREE.Vector3(-0.03, -0.03, 0), new THREE.Vector3(0.035, 0.1, 0), new THREE.Vector3(0, 0.22, 0)]);
    return new THREE.TubeGeometry(curve, 12, 0.012, 4, false);
  });
  add(center, stem, m.brass, 0, 0, 0);
  const leafGeo = kit.shared('apothecary:carving-leaf', () => new THREE.SphereGeometry(1, 6, 4));
  for (let i = 0; i < 6; i += 1) {
    const side = i % 2 ? 1 : -1;
    const yy = -0.13 + i * 0.057;
    const o = add(center, leafGeo, m.brass, side * 0.067, yy, 0);
    o.scale.set(0.072, 0.026, 0.011);
    o.rotation.z = side * 0.56;
  }
  if (variant % 2 === 0) {
    const ring = add(center, kit.shared('apothecary:carving-ring', () => new THREE.TorusGeometry(0.23, 0.009, 4, 32)), m.brass, 0, 0.015, 0);
    ring.scale.set(0.73, 1, 1);
  }
};
